export type TextDirection = "ltr" | "rtl";
export type ContentDirection = TextDirection | "auto";

const RTL_LANGUAGE_CODES = new Set([
  "ar",
  "arc",
  "azb",
  "bal",
  "ckb",
  "dv",
  "fa",
  "he",
  "ks",
  "lrc",
  "mid",
  "mzn",
  "nqo",
  "pa",
  "pnb",
  "ps",
  "rhg",
  "sd",
  "sdh",
  "syr",
  "ug",
  "ur",
  "yi",
]);

const RTL_SCRIPT_CODES = new Set([
  "adlm",
  "arab",
  "hebr",
  "mand",
  "nkoo",
  "rohg",
  "syrc",
  "thaa",
]);

const AMBIGUOUS_DIRECTION_LANGUAGE_CODES = new Set(["pa"]);
const RTL_LETTER =
  /[\p{Script=Adlam}\p{Script=Arabic}\p{Script=Hebrew}\p{Script=Hanifi_Rohingya}\p{Script=Mandaic}\p{Script=Nko}\p{Script=Syriac}\p{Script=Thaana}]/u;
const LETTER = /\p{Letter}/u;

const _languageParts = (language: string): string[] =>
  language.trim().toLowerCase().replace(/_/g, "-").split("-").filter(Boolean);

export const getLanguageDirection = (language: string): TextDirection => {
  const parts = _languageParts(language);
  if (parts.some((part) => RTL_SCRIPT_CODES.has(part))) return "rtl";
  if (parts[0] === "pa" && parts.includes("guru")) return "ltr";
  return RTL_LANGUAGE_CODES.has(parts[0] || "") ? "rtl" : "ltr";
};

export const getContentDirection = (
  language: string | null | undefined,
): ContentDirection => {
  const normalized = language?.trim().toLowerCase();
  if (!normalized || normalized === "auto" || normalized === "unknown") {
    return "auto";
  }
  const parts = _languageParts(normalized);
  if (
    parts.length === 1 &&
    AMBIGUOUS_DIRECTION_LANGUAGE_CODES.has(parts[0])
  ) {
    return "auto";
  }
  return getLanguageDirection(normalized);
};

export const getTextDirection = (
  text: string,
  fallback: TextDirection = "ltr",
): TextDirection => {
  for (const character of text) {
    if (RTL_LETTER.test(character)) return "rtl";
    if (LETTER.test(character)) return "ltr";
  }
  return fallback;
};

// Image/PDF imports keep whatever language was picked at import (the server
// reads documents without it), so an Arabic PDF imported as "English" must
// take its direction from its own text.
export const getTranscriptDirection = (transcript: {
  type?: string;
  language?: string | null;
}): ContentDirection =>
  transcript.type === "take_photo_or_import_image_or_pdf"
    ? "auto"
    : getContentDirection(transcript.language);
